import { Box, EmptyState, FlexBox, Text } from '@dixa/lib-shared-habla';
import type {
  ConversationFacebookMessengerAttributes,
  ConversationFacebookMessengerMessageAddedEvent,
} from 'lib-shared-types-api';
import { useCallback, useState } from 'react';

import { ConversationEventGroupConfigBase } from 'components/conversation/conversationEventGroups/conversationEventGroups.types';
import { getMessageFileType } from 'utils/conversation/getMessageFileType';

import { ConversationTextualMessage } from '../../textualMessage/conversationTextualMessage';
import { ConversationTextualMessagePropBase } from '../../textualMessage/conversationTextualMessage.types';

import { InstagramStoryImage } from './components/storyImage/instagramStoryImage';
import { InstagramStoryVideo } from './components/storyVideo/instagramStoryVideo';
import styles from './conversationInstagramStoryEventGroup.module.scss';

/** Represents the conversation instagram story event group configuration. */
export type ConversationInstagramEventGroupConfig =
  ConversationEventGroupConfigBase<
    'group:instagram_story',
    Pick<ConversationInstagramStoryEventGroupProps, 'hideGutterActions'>
  >;

type ConversationInstagramStoryEventGroupProps =
  ConversationTextualMessagePropBase<
    ConversationFacebookMessengerMessageAddedEvent,
    {
      /** The conversation attributes. */
      attributes: ConversationFacebookMessengerAttributes;
    }
  >;

/**
 * Represents the component for displaying a conversation instagram story event
 * group.
 */
export const ConversationInstagramStoryEventGroup = ({
  attributes: { instagram_account_username, instagram_account_name },
  author,
  className,
  events,
  hideGutterActions,
}: ConversationInstagramStoryEventGroupProps): JSX.Element => {
  const [isStoryExpired, setIsStoryExpired] = useState(false);

  const handleStoryError = useCallback(() => {
    setIsStoryExpired(true);
  }, []);

  const getStoryComponent = useCallback(
    (event: ConversationFacebookMessengerMessageAddedEvent) => {
      if (!event.attached_files || isStoryExpired) {
        return (
          <FlexBox
            background="neutral.100"
            minHeight="200px"
            alignItems="center"
            justifyContent="center"
          >
            <EmptyState
              title="Story unavailable"
              description="You can no longer see this story because it expired."
            />
          </FlexBox>
        );
      }

      const storyAttachment = event.attached_files?.[0];
      const messageFileType = getMessageFileType(storyAttachment);

      if (messageFileType === 'image') {
        return (
          <InstagramStoryImage
            attachment={storyAttachment}
            onError={handleStoryError}
          />
        );
      }

      if (messageFileType === 'video') {
        return (
          <InstagramStoryVideo
            attachment={storyAttachment}
            onError={handleStoryError}
          />
        );
      }

      return (
        <FlexBox
          background="neutral.100"
          minHeight="200px"
          alignItems="center"
          justifyContent="center"
        >
          <EmptyState
            title="Media not supported"
            description={`The ${messageFileType} media type is not supported.`}
          />
        </FlexBox>
      );
    },
    [handleStoryError, isStoryExpired],
  );

  return (
    <ConversationTextualMessage
      className={className}
      author={author}
      hideGutterActions={hideGutterActions}
      authorDescriptionSlot={
        <Text.Paragraph variant="4" subdued>
          {events[0].text
            ? 'commented on your story'
            : `@${instagram_account_username} was mentioned in a story`}
        </Text.Paragraph>
      }
      timestamp={events[0].created_at}
      variant={
        events[0].direction === 'outbound'
          ? 'textualOutbound'
          : 'textualInbound'
      }
    >
      <FlexBox
        flexDirection="row"
        gap="200"
        style={{ wordBreak: 'break-word' }}
      >
        {events.map((event) => (
          <FlexBox key={event.id} flexDirection="column" gap="300" flex="1">
            {event.text && <Box>{event.text}</Box>}

            <FlexBox
              flexDirection="column"
              borderWidth="100"
              borderColor="neutral.200"
            >
              <Box padding="300">
                <Text.Heading variant="heading-4">
                  {instagram_account_name}
                </Text.Heading>
              </Box>
              <Box className={styles.storyAttachment}>
                {getStoryComponent(event)}
              </Box>
            </FlexBox>
          </FlexBox>
        ))}
      </FlexBox>
    </ConversationTextualMessage>
  );
};
